import styles from "./EBook.module.less";
import CommonNavBar from "views/components/CommonNavBar";
import { useNavigate } from "react-router-dom";
import { useState, useRef, useEffect, useCallback } from "react";
import { getLibraryBookListV3, getBookFilterCondition } from "api/picture";
import { Toast } from "react-vant";
import useScrollLoad from "utils/useScrollLoad";
import BottomText from "views/picture/home/bottom-text/BottomText";
import DSBridge from "dsbridge";
import { debounce } from "lodash";
import CommonModal from "views/components/CommonModal";
import { useSizeDetector } from "@/utils/sizeDetector";
import { navigateToSignIn } from "utils/tokenTools";

const EBook = () => {
  const navigate = useNavigate();
  const [bookList, setBookList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [lockShow, setLockShow] = useState(null);

  const [optionList, setOptionList] = useState([]);
  const [option, setOption] = useState("");

  const LoadingRef = useRef(null);
  const isInitializedRef = useRef(false);

  const { isMobile, isTabletPortrait, isTabletLandscape } = useSizeDetector();

  const STORAGE_KEY = "picture_ebook_filters";

  const saveToLocalStorage = useCallback((params) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(params));
    } catch (error) {
      console.log("保存到本地存储失败:", error);
    }
  }, []);

  const getSavedParams = useCallback(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          option: parsed.option || "",
        };
      }
    } catch (error) {
      console.log("从本地存储读取失败:", error);
    }
    return {
      option: "",
    };
  }, []);

  const handleBookCallback = (data) => {
    try {
      data = JSON.parse(data);
      const idSet_started = Array.isArray(data.is_started)
        ? new Set(data.is_started.map((item) => item))
        : new Set();
      const idSet_is_completed = Array.isArray(data.is_completed)
        ? new Set(data.is_completed.map((item) => item))
        : new Set();

      setBookList((prev) => {
        return prev.map((item) => {
          return {
            ...item,
            reading_status: {
              ...(item.reading_status || {}),
              is_started:
                idSet_started.has(item.syllabus_id) ||
                item?.reading_status?.is_started,
              is_completed:
                idSet_is_completed.has(item.syllabus_id) ||
                item?.reading_status?.is_completed,
            },
          };
        });
      });
    } catch (error) {
      console.log(error);
    }
  };

  const handleClickBook = debounce((item) => {
    if (!localStorage.getItem("access_token")) {
      navigateToSignIn("#/login/logon");
      return;
    }
    if (item.is_lock === 1) {
      setLockShow(item.wechat_link);
      return;
    }
    if (DSBridge.hasNativeMethod("book.startEbook")) {
      DSBridge.call(
        "book.startEbook",
        {
          syllabus_id: item.syllabus_id,
          token: localStorage.getItem("access_token"),
          refreshToken: localStorage.getItem("refresh_token"),
          callback: "onBookStatusChange",
        },
        (back) => {
          console.log(back);
        },
      );
    }
  }, 200);

  const getBookList = async (overrides = {}) => {
    setIsLoading(true);
    try {
      const currentOpt = overrides.option ?? option;
      const params = {
        page: 1,
        limit: 60,
      };
      if (currentOpt !== "") {
        params.tag_ids = currentOpt;
      }
      const bookRes = await getLibraryBookListV3(params);

      if (bookRes.code === 200) {
        const newBooks = bookRes.data.books || bookRes.data || [];
        setBookList(newBooks);
        setCurrentPage(2);
        setHasMore(newBooks.length >= 60);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMoreBooks = useCallback(async () => {
    if (isLoading || !hasMore) return;

    setIsLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: 60,
      };
      if (option !== "") {
        params.tag_ids = option;
      }
      const bookRes = await getLibraryBookListV3(params);

      if (bookRes.code === 200) {
        const newBooks = bookRes.data.books || bookRes.data || [];
        if (newBooks.length > 0) {
          setBookList((prev) => [...prev, ...newBooks]);
          setCurrentPage((prev) => prev + 1);
          if (newBooks.length < 60) setHasMore(false);
        } else {
          setHasMore(false);
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, isLoading, hasMore, option]);

  const initData = async () => {
    try {
      const savedParams = getSavedParams();
      const conditionRes = await getBookFilterCondition({ category_type: 1 });
      console.log("获取到的图书过滤条件数据:", conditionRes.data);

      let currentOption = savedParams.option;

      // 解析接口返回的 book_level 数据
      if (
        conditionRes.code === 200 &&
        conditionRes.data?.tags?.book_level?.items?.length > 0
      ) {
        const bookLevelItems = [
          { id: "", name: "全部" },
          ...conditionRes.data.tags.book_level.items,
        ];
        setOptionList(bookLevelItems);

        const validSeries = bookLevelItems.find(
          (item) => item.id === currentOption,
        );
        if (!validSeries) {
          currentOption = "";
        }
      } else {
        // 请求失败或者没有数据时的降级处理
        setOptionList([{ id: "", name: "全部" }]);
        currentOption = "";
      }

      setOption(currentOption);
      saveToLocalStorage({ option: currentOption });

      await getBookList({ option: currentOption });

      isInitializedRef.current = true;
    } catch (error) {
      console.log("初始化数据失败:", error);
      isInitializedRef.current = true;
    }
  };

  const handleOptionChange = (newOption) => {
    if (isLoading) {
      return;
    }
    setOption(newOption);
    saveToLocalStorage({ option: newOption });
  };

  const handleClickWx = () => {
    if (!lockShow) {
      return Toast.info("暂无跳转链接，请稍后重试");
    }
    try {
      if (DSBridge.hasNativeMethod("route.navigation")) {
        DSBridge.call(
          "route.navigation",
          {
            route: "miniProgram",
            mid: "gh_ce82ce6b1629",
            path: lockShow,
          },
          (back) => {
            console.log(back);
          },
        );
      }
    } catch (e) {
      console.log(e);
    }
    setLockShow(false);
  };

  const containerRef = useScrollLoad(loadMoreBooks, 100);

  useEffect(() => {
    initData();

    DSBridge.register("onBookStatusChange", (data) => {
      handleBookCallback(data);
    });

    return () => {
      if (LoadingRef.current?.clear) {
        LoadingRef.current?.clear();
        LoadingRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!isInitializedRef.current) {
      return;
    }

    setCurrentPage(1);
    setHasMore(true);
    setBookList([]);
    getBookList();
  }, [option]);

  return (
    <div className={styles["e-book-container"]}>
      <CommonNavBar
        backgroundColor="transparent"
        onClickBack={() => navigate(-1)}
      />
      {isMobile && (
        <>
          <div className={styles["header-row"]}>
            <div className={styles["header-title"]}>学习手册</div>
          </div>
          <div className={styles["option-box"]}>
            {optionList.map((item, index) => (
              <div
                className={`${styles["option-box-item"]} ${
                  option === item.id ? styles["option-box-item--selected"] : ""
                }`}
                key={`${index}-option-item`}
                onClick={() => handleOptionChange(item.id)}
              >
                {item.name}
              </div>
            ))}
          </div>
        </>
      )}
      {(isTabletPortrait || isTabletLandscape) && (
        <>
          <div className={styles["header-row"]}>
            <div className={styles["header-title"]}>学习手册</div>
          </div>
          <div className={styles["filter-box"]}>
            <div className={styles["option-box"]}>
              {optionList.map((item, index) => (
                <div
                  className={`${styles["option-box-item"]} ${
                    option === item.id
                      ? styles["option-box-item--selected"]
                      : ""
                  }`}
                  key={`${index}-option-item`}
                  onClick={() => handleOptionChange(item.id)}
                >
                  {item.name}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <div className={styles["books-box"]} ref={containerRef}>
        {bookList.map((item, index) => (
          <div
            className={styles["content-item"]}
            key={`${index}-content-item`}
            onClick={() => handleClickBook(item)}
          >
            <div className={styles["item-box"]}>
              <div className={styles["item-box-content"]}>
                <img
                  className={styles["item-box-content-img"]}
                  src={item.cover_url}
                />
                {item.is_lock === 1 && (
                  <div className={styles["item-box-content-lock"]}>
                    <img
                      className={styles["lock-img"]}
                      src={require("@/images/picture/e-book/lock.png")}
                    />
                  </div>
                )}
              </div>
              {(item?.reading_status?.is_started ||
                item?.reading_status?.is_completed) && (
                <img
                  className={styles["item-box-icon"]}
                  src={require(
                    `@/images/picture/e-book/${
                      item?.reading_status?.is_completed
                        ? "finish-read-phone.png"
                        : "is-read-phone.png"
                    }`,
                  )}
                />
              )}
              <div className={styles["item-box-text"]}>
                <span>{item.book_name}</span>
              </div>
            </div>
          </div>
        ))}

        <BottomText
          isLoading={isLoading}
          dataList={bookList}
          hasMore={hasMore}
        />
      </div>
      <CommonModal isOpen={lockShow}>
        <div className={styles["pop-container--lock"]}>
          <img
            onClick={() => setLockShow(false)}
            className={styles["lock-close"]}
            src={require("@/images/picture/e-book/lock-close.png")}
          />
          <img
            onClick={() => handleClickWx()}
            className={styles["lock-button"]}
            src={require("@/images/picture/e-book/lock-button.png")}
          />
        </div>
      </CommonModal>
    </div>
  );
};

export default EBook;
